import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import ViewCompanyProfile from "marketplace/companies/profiles/_views/view-company-profile";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualByUserId } from "marketplace/individuals/_stores/individuals-redux-slice";
import ViewTeamProfile from "marketplace/team-profile/_views/view-team-profile";
import { useGetTeamByIdQuery } from "marketplace/teams/_api/marketplace-team-api";
import { getTeamById } from "marketplace/teams/_store/marketplace-teams-redux-slice";
import MarketplaceTeam from "marketplace/teams/team";
import { MouseEvent } from "react";
import { useSelector } from "react-redux";
import Session from "users/__session/session";
import { Masked, MaskedTeam } from "work/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { getUpdatedProposalBuilderByProposalId } from "work/proposals/_store/proposals-redux-slice";
import Proposal from "work/proposals/proposal";
import ViewVendorTeam from "work/work-agreements/teams/vendor-team/_views/vendor-team-view";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

const CellContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
  minWidth: 0,
}));

const DetailLine = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.75rem",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.primary,
  cursor: "pointer",
  display: "inline",
  overflow: "hidden",
  textDecoration: "underline",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

type VendorTeamLinkProps = {
  displayMode?: "full" | "compact" | "minimal";
  proposal: Proposal;
  session: Session;
  /**
   * The team to show instead of the proposal's own: the comparison shows a hired proposal's team as
   * it was hired, not as the work stands today (#6633).
   */
  team?: VendorTeam | MaskedTeam | null;
};

export default function VendorTeamLink(props: Readonly<VendorTeamLinkProps>) {
  const { proposal, session } = props;
  const displayMode = props.displayMode ?? "full";

  const { openDialog, closeDialog } = useDialog();
  const updatedBuilderJson = useSelector(getUpdatedProposalBuilderByProposalId(proposal.id));
  const updatedBuilder = ProposalBuilder.fromJSON({
    json: updatedBuilderJson,
    session,
  });
  const updatedBuilderTeam = updatedBuilder?.currentSpec.workAgreement.vendorTeam;
  let team = Masked.isPresentNotMasked(updatedBuilderTeam) ? updatedBuilderTeam : proposal.vendorTeam;
  if (props.team !== undefined) team = props.team;
  const marketplaceTeamId = Masked.isPresentNotMasked(team) ? team.marketplaceId : undefined;
  const marketplaceTeam = useSelector(getTeamById(marketplaceTeamId));
  const marketplaceTeamQuery = useGetTeamByIdQuery(
    marketplaceTeamId ? { teamId: marketplaceTeamId.value } : skipToken,
    { skip: !marketplaceTeamId || !!marketplaceTeam },
  );
  const fetchedMarketplaceTeam = marketplaceTeamQuery.data
    ? MarketplaceTeam.fromJSON(marketplaceTeamQuery.data)
    : undefined;
  const resolvedMarketplaceTeam = marketplaceTeam ?? fetchedMarketplaceTeam;
  const leaderUserId = Masked.isPresentNotMasked(team) ? team.leader?.userId : undefined;
  const leaderIndividual = useSelector(getIndividualByUserId(leaderUserId));
  const leaderIndividualJson = useSelector((state: RootState) =>
    leaderUserId ? state.individuals.byUserId.entries[leaderUserId.value] : undefined,
  );
  const leaderQuery = useGetIndividualsByUserIdsQuery(
    leaderUserId ? { userIds: [leaderUserId.value] } : skipToken,
    { skip: !leaderUserId || !!leaderIndividual },
  );

  function openTeamMembers(event: MouseEvent) {
    event.stopPropagation();

    if (!team || Masked.isMasked(team)) return;

    openDialog({
      title: "Team Members",
      component: <Loader />,
    });

    if (Masked.isMasked(team)) return;

    closeDialog();

    openDialog({
      title: `${proposal.name} Team`,
      MuiProps: { maxWidth: "md" },
      component: <ViewVendorTeam leader={team.leader} members={team.members} />,
    });
  }

  function openMarketplaceTeam(event: MouseEvent) {
    event.stopPropagation();
    if (!marketplaceTeamId) return;

    openDialog({
      component: <ViewTeamProfile teamId={marketplaceTeamId} />,
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }

  function openLeaderCompany(event: MouseEvent, companyId: Guid) {
    event.stopPropagation();

    openDialog({
      component: <ViewCompanyProfile companyId={companyId} />,
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }

  if (!team) return null;
  if (Masked.isMasked(team)) return <span>(masked)</span>;

  const membersCount = team.members.length + (team.leader ? 1 : 0);
  const memberText = `${membersCount} ${membersCount === 1 ? "Member" : "Members"}`;
  let leaderName = leaderIndividual?.fullName ?? "Unknown leader";
  if (leaderQuery.isLoading) {
    leaderName = "Loading leader";
  } else if (leaderQuery.isError) {
    leaderName = "Unknown leader";
  }
  const companyId = Guid.fromJSON(leaderIndividualJson?.companyId);
  const companyName = leaderIndividual?.entityName ?? leaderIndividualJson?.companyName;
  let teamName = resolvedMarketplaceTeam?.profile.name;
  if (marketplaceTeamQuery.isLoading) {
    teamName = "Loading team";
  } else if (marketplaceTeamQuery.isError) {
    teamName = "Team profile unavailable";
  }
  const leaderDetails = (
    <>
      {leaderName}
      {companyName && (
        <>
          {" at "}
          {companyId ? (
            <StyledLink onClick={(event) => openLeaderCompany(event, companyId)}>
              {companyName}
            </StyledLink>
          ) : (
            companyName
          )}
        </>
      )}
    </>
  );

  if (displayMode === "minimal") {
    return (
      <StyledLink onClick={openTeamMembers}>
        View
      </StyledLink>
    );
  }

  return (
    <CellContainer>
      {marketplaceTeamId && teamName ? (
        <StyledLink onClick={openMarketplaceTeam}>
          {teamName}
        </StyledLink>
      ) : (
        <StyledLink onClick={openTeamMembers}>{memberText}</StyledLink>
      )}
      {displayMode === "full" && (
        <DetailLine>{leaderDetails}</DetailLine>
      )}
    </CellContainer>
  );
}
